import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Activity, Clock } from 'lucide-react';
import { useAppStore } from 'store/appStore';
import { MeetingUsageStats } from 'api-types';
import { SpinnerLoader } from 'components/Loader/SpinnerLoader';
import { DatePicker } from 'components/DatePicker';
import { convertMonthToDateRange, getCurrentMonthString } from '../utils/utils';
import { ErrorCard } from '../../../components/ErrorHandling';

export default function UsageDashboard() {
  const navigate = useNavigate();
  const { meetingId } = useParams<{ meetingId: string }>();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [usageStats, setUsageStats] = useState<MeetingUsageStats | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    if (meetingId) {
      const stored = sessionStorage.getItem(`usage_selectedMonth_${meetingId}`);
      if (stored) {
        return stored;
      }
    }
    return getCurrentMonthString();
  });

  const fetchMeetingUsageStats = useAppStore(state => state.fetchMeetingUsageStats);

  const loadUsageStats = useCallback(async () => {
    if (!meetingId || !selectedMonth) return;

    const dateRange = convertMonthToDateRange(selectedMonth);

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchMeetingUsageStats({
        meetingId,
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
      });

      setUsageStats(response.stats || null);
    } catch (err) {
      setError('Failed to load usage statistics. Please try again later.');
      setUsageStats(null);
    } finally {
      setIsLoading(false);
    }
  }, [meetingId, selectedMonth, fetchMeetingUsageStats]);

  useEffect(() => {
    loadUsageStats();
  }, [loadUsageStats]);

  const handleMonthChange = (monthValue: string) => {
    setSelectedMonth(monthValue);
    if (meetingId) {
      sessionStorage.setItem(`usage_selectedMonth_${meetingId}`, monthValue);
    }
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return <SpinnerLoader />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-6">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft size={20} />
              Back
            </button>
          </div>
          <ErrorCard
            title="Unable to load usage statistics"
            message={error}
            onRetry={loadUsageStats}
            onBack={handleBack}
            isRetrying={isLoading}
            showBackButton={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors rounded-lg hover:bg-gray-50"
              >
                <ArrowLeft size={20} />
                Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Usage</h1>
            </div>
            <div className="flex items-center gap-4">
              <DatePicker selectedMonth={selectedMonth} onMonthChange={handleMonthChange} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {usageStats && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Activity className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Meetings</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {usageStats.totalMeetings || 0}
                    </p>
                  </div>
                </div>
                <div className="text-xs text-gray-400">Meetings this month</div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Duration</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatDuration(Math.round((usageStats.totalDuration || 0) / (1000 * 60)))}
                    </p>
                  </div>
                </div>
                <div className="text-xs text-gray-400">Time spent this month</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
